import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { HardHat, ArrowRight, Gauge, CheckCircle2, Lock } from "lucide-react";
import ReadinessGauge from "@/components/ReadinessGauge";

const TRADES = [
  { v: "general", l: "General Contractor" }, { v: "electrical", l: "Electrical" },
  { v: "hvac", l: "HVAC" }, { v: "plumbing", l: "Plumbing" }, { v: "roofing", l: "Roofing" },
  { v: "concrete", l: "Concrete" }, { v: "civil", l: "Civil & Heavy" }, { v: "specialty", l: "Specialty" },
];
const CERTS = ["8(a)", "MBE", "WBE", "SDVOSB", "WOSB", "HUBZone", "DBE", "ISO 9001"];
const inputCls = "rounded-md border border-slate-200 px-3.5 py-2.5 text-sm w-full bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500";
const labelCls = "text-sm font-medium text-slate-700 mb-1.5 block";

const empty = {
  email: "", company_name: "", trade: "general", state: "", years_in_business: 0,
  has_license: false, certifications: [], bonding_capacity: 0,
  has_general_liability: false, has_workers_comp: false, emr: 1.0,
  gov_contracts_completed: 0, annual_revenue: 0, employee_count: 0,
};

export default function FreeScore() {
  const { login } = useAuth();
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleCert = (c) => setF((p) => ({ ...p, certifications: p.certifications.includes(c) ? p.certifications.filter((x) => x !== c) : [...p.certifications, c] }));

  const submit = async () => {
    if (!f.email.trim() || !f.email.includes("@")) { toast.error("Enter a valid email to see your score"); return; }
    if (!f.company_name.trim()) { toast.error("Add your company name"); return; }
    setLoading(true);
    try {
      const r = await api.post("/readiness/free", {
        ...f,
        bonding_capacity: Number(f.bonding_capacity) || 0,
        annual_revenue: Number(f.annual_revenue) || 0,
        employee_count: Number(f.employee_count) || 0,
        gov_contracts_completed: Number(f.gov_contracts_completed) || 0,
        years_in_business: Number(f.years_in_business) || 0,
        emr: Number(f.emr) || 1.0,
      });
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not compute your score");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="free-score-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading font-bold text-slate-900">
            <span className="h-8 w-8 rounded-lg bg-emerald-900 text-white flex items-center justify-center"><HardHat className="h-4.5 w-4.5" /></span>
            BidMatchAI
          </Link>
          <button onClick={login} data-testid="free-signin-button" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Sign in</button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-12">
        {!result ? (
          <>
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-emerald-800">
                <Gauge className="h-4 w-4" /> Free Contractor Readiness Score
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-5">What's your bid-readiness score?</h1>
              <p className="text-slate-500 mt-4 max-w-xl mx-auto">Answer a few questions and get your 0–1000 Contractor Readiness Score in seconds — see exactly where you stand before chasing your next government contract. No credit card.</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8 space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Work email *</label><input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="fs-email" placeholder="you@company.com" className={inputCls} /></div>
                <div><label className={labelCls}>Company name *</label><input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="fs-company" placeholder="Acme Builders LLC" className={inputCls} /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><label className={labelCls}>Trade</label><select value={f.trade} onChange={(e) => set("trade", e.target.value)} data-testid="fs-trade" className={inputCls}>{TRADES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}</select></div>
                <div><label className={labelCls}>State</label><input value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="fs-state" placeholder="TX" className={inputCls} /></div>
                <div><label className={labelCls}>Years in business</label><input type="number" value={f.years_in_business} onChange={(e) => set("years_in_business", e.target.value)} data-testid="fs-years" className={inputCls} /></div>
              </div>

              <div>
                <label className={labelCls}>Certifications / set-asides held</label>
                <div className="flex flex-wrap gap-2" data-testid="fs-certs">
                  {CERTS.map((c) => (
                    <button key={c} type="button" onClick={() => toggleCert(c)} data-testid={`fs-cert-${c}`} className={`rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors ${f.certifications.includes(c) ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{c}</button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div><label className={labelCls}>Single-project bonding ($)</label><input type="number" value={f.bonding_capacity} onChange={(e) => set("bonding_capacity", e.target.value)} data-testid="fs-bonding" placeholder="2500000" className={inputCls} /></div>
                <div><label className={labelCls}>Annual revenue ($)</label><input type="number" value={f.annual_revenue} onChange={(e) => set("annual_revenue", e.target.value)} data-testid="fs-revenue" placeholder="5000000" className={inputCls} /></div>
                <div><label className={labelCls}>Employees</label><input type="number" value={f.employee_count} onChange={(e) => set("employee_count", e.target.value)} data-testid="fs-employees" className={inputCls} /></div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><label className={labelCls}>Gov contracts completed</label><input type="number" value={f.gov_contracts_completed} onChange={(e) => set("gov_contracts_completed", e.target.value)} data-testid="fs-govcontracts" className={inputCls} /></div>
                <div><label className={labelCls}>EMR (safety rating)</label><input type="number" step="0.01" value={f.emr} onChange={(e) => set("emr", e.target.value)} data-testid="fs-emr" placeholder="1.0" className={inputCls} /></div>
              </div>

              <div className="flex flex-wrap gap-5 pt-1">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.has_license} onChange={(e) => set("has_license", e.target.checked)} data-testid="fs-license" className="h-4 w-4 accent-emerald-700" /> Active contractor license</label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.has_general_liability} onChange={(e) => set("has_general_liability", e.target.checked)} data-testid="fs-gl" className="h-4 w-4 accent-emerald-700" /> General liability insurance</label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={f.has_workers_comp} onChange={(e) => set("has_workers_comp", e.target.checked)} data-testid="fs-wc" className="h-4 w-4 accent-emerald-700" /> Workers' comp</label>
              </div>

              <button onClick={submit} disabled={loading} data-testid="fs-submit" className="w-full bg-emerald-900 text-white rounded-md py-3.5 text-sm font-medium hover:bg-emerald-800 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {loading ? "Calculating…" : <>Get my free contractor readiness score <ArrowRight className="h-4 w-4" /></>}
              </button>
              <p className="text-xs text-slate-400 text-center">We'll email your score and tips. No spam, unsubscribe anytime.</p>
            </div>
          </>
        ) : (
          <div data-testid="fs-result" className="space-y-8">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Your Contractor Readiness</p>
              <div className="flex justify-center"><ReadinessGauge score={result.score} tier={result.tier} size={240} /></div>
              <p className="text-slate-500 mt-5 max-w-md mx-auto">
                {result.score >= 800 ? "You're bid-ready. Now find the contracts you're most likely to win." : result.score >= 500 ? "You're developing. A few targeted moves will unlock bigger opportunities." : "You're early-stage. Here's exactly what to build next to qualify for more bids."}
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-7">
              <h3 className="font-heading text-lg font-semibold mb-5">Readiness breakdown</h3>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {Object.entries(result.breakdown).map(([k, v]) => (
                  <div key={k}>
                    <div className="flex justify-between text-sm mb-1.5"><span className="text-slate-700 font-medium">{k}</span><span className="font-data text-slate-500">{v.score}/{v.max}</span></div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-emerald-600" style={{ width: `${(v.score / v.max) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-emerald-900 text-white rounded-2xl p-8 text-center">
              <h3 className="font-heading text-2xl font-bold">Now win more of them.</h3>
              <p className="text-slate-300 mt-2 max-w-lg mx-auto">Create your free account to get matched opportunities, AI win-probability scores on every bid, and a step-by-step roadmap to raise this number.</p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={login} data-testid="fs-create-account" className="bg-white text-emerald-900 rounded-md px-7 py-3.5 text-sm font-semibold hover:bg-slate-100 transition-colors inline-flex items-center justify-center gap-2">Create my free account <ArrowRight className="h-4 w-4" /></button>
                <button onClick={() => { setResult(null); }} data-testid="fs-recalculate" className="bg-white/10 text-white rounded-md px-7 py-3.5 text-sm font-medium hover:bg-white/20 transition-colors">Recalculate</button>
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-300">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-lime-400" /> Free forever plan</span>
                <span className="inline-flex items-center gap-1.5"><Lock className="h-4 w-4 text-lime-400" /> No credit card</span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
